import React, { useEffect, useState, useRef } from "react";
import { Search, ChevronDown, User, X, MoreVertical, Lock, Phone, Check, Copy } from "lucide-react";
import { GetMyDeals } from "../Services/DealService";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SkeletonTheme } from "react-loading-skeleton";
import {
  ShortlistCandidate,
  RejectCandidate,
  BackoutCandidate,
  SheduleInterview,
  SelectCandidate,
  MoveCandidateToBGV,
  ReleaseCandidateOffer,
  HireCandidate,
} from "../Services/DealService";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import DealActionDrawer from "../Modals/DealActionDrawer";
import { formatCTC } from "../../../../Utils/FormUtils";
import { GetDealHistory } from "../Services/DealService";
import DealHistoryDrawer from "../Modals/DealHistoryDrawer";
import ViewResumeDrawer from "../../../../Common/Recruitment/Modals/ViewResumeDrawer";

const DEAL_STATUS = {
  CV_SENT: 1,
  SHORTLISTED: 2,
  INTERVIEW: 3,
  SELECTED: 4,
  BGV: 5,
  OFFER: 6,
  HIRED: 7,
  REJECTED: 8,
  BACKOUT: 9,
};

const DEAL_ACTIONS = {
  "CV Sent": ["Shortlist", "Reject", "Backout"],

  Shortlisted: ["Schedule Interview", "Backout"],

  "Interview Sheduled": ["Schedule Interview", "Select", "Reject", "Backout"],

  Selected: ["BGV", "Release Offer", "Backout"],

  BGV: ["Release Offer", "Hire", "Backout"],

  "Offer Released": ["BGV", "Hire", "Backout"],

  Hired: ["Backout"],

  Rejected: [],

  Backout: [],
};

const ACTION_META = {
  shortlist: {
    label: "Shortlist",
    color: "green",
  },
  reject: {
    label: "Reject",
    color: "red",
  },
  scheduleInterview: {
    label: "Schedule Interview",
    color: "blue",
  },
  select: {
    label: "Select",
    color: "green",
  },
  moveToBGV: {
    label: "Move to BGV",
    color: "purple",
  },
  releaseOffer: {
    label: "Release Offer",
    color: "indigo",
  },
  hire: {
    label: "Hire",
    color: "emerald",
  },
  backout: {
    label: "Backout",
    color: "orange",
  },
};

const STATUS_BADGE_CLASSES = {
  "CV Sent": "bg-slate-100 text-slate-700 border-slate-200",
  Shortlisted: "bg-blue-100 text-blue-700 border-blue-200",
  "Interview Sheduled": "bg-amber-100 text-amber-700 border-amber-200",
  Selected: "bg-emerald-100 text-emerald-700 border-emerald-200",
  BGV: "bg-purple-100 text-purple-700 border-purple-200",
  "Offer Released": "bg-indigo-100 text-indigo-700 border-indigo-200",
  Hired: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
  Backout: "bg-orange-100 text-orange-700 border-orange-200",
};

const getStatusBadgeClass = (status) =>
  STATUS_BADGE_CLASSES[status] ||
  "bg-slate-100 text-slate-700 border-slate-200";

const MyDeals = () => {
  const actionMenuRef = useRef(null);

  const [deals, setDeals] = useState([]);
  const [searchF, setSearchF] = useState("");

  const [chunkNumber, setChunkNumber] = useState(1);
  const [totalChunks, setTotalChunks] = useState(1);

  const [expandedDeal, setExpandedDeal] = useState(null);

  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [totalRecords, setTotalRecords] = useState(0);

  const [initialLoading, setInitialLoading] = useState(true);
  const [chunkLoading, setChunkLoading] = useState(false);

  const [localResults, setLocalResults] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const [activeSearch, setActiveSearch] = useState("");
  const [dbSearching, setDbSearching] = useState(false);

  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [alert, setAlert] = useState(null);

  const [historyDeal, setHistoryDeal] = useState(null);
  const [openResumeDrawer, setOpenResumeDrawer] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [copiedLeadId, setCopiedLeadId] = useState(null);

  const fetchDeals = async (chunk = 1, reset = false, searchParam = "") => {
    try {
      if (reset) {
        setInitialLoading(true);
      } else {
        setChunkLoading(true);
      }

      const res = await GetMyDeals(chunk, searchParam);

      setDeals(res?.deals || []);
      setChunkNumber(chunk);
      setTotalChunks(res?.totalChunks || 1);
      setTotalRecords(res?.totalRecords || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setInitialLoading(false);
      setChunkLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals(1, true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setOpenActionMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onViewHistory = (deal) => {
    setHistoryDeal(deal);
  };

  const handleLocalSearch = async () => {
    const value = searchF.trim().toLowerCase();

    if (!value) {
      setLocalResults([]);
      setSearchPerformed(false);
      return;
    }

    const results = deals.filter((deal) => {
      return (
        deal.name?.toLowerCase().includes(value) ||
        deal.targetOrganization?.toLowerCase().includes(value) ||
        deal.targetRole?.toLowerCase().includes(value) ||
        deal.phoneNo?.toLowerCase().includes(value) ||
        deal.dealStatus?.toLowerCase().includes(value)
      );
    });

    if (results.length > 0) {
      setPage(1);

      setSearchPerformed(true);

      setLocalResults(results);

      return;
    }

    try {
      setDbSearching(true);

      setPage(1);

      setLocalResults([]);

      setSearchPerformed(false);

      setActiveSearch(value);

      await fetchDeals(1, true, value);
    } finally {
      setDbSearching(false);
    }
  };

  const searchDatabase = async () => {
    const query = searchF.trim();

    if (!query) return;

    setPage(1);

    setLocalResults([]);

    setSearchPerformed(false);

    setActiveSearch(query);

    try {
      setDbSearching(true);

      await fetchDeals(1, true, query);
    } finally {
      setDbSearching(false);
    }
  };

  const handleReset = () => {
    setSearchF("");
    setPage(1);
    setLocalResults([]);
    setSearchPerformed(false);
    setActiveSearch("");
    fetchDeals(1, true, "");
  };

  const toggleExpand = (dealUID) => {
    setExpandedDeal(expandedDeal === dealUID ? null : dealUID);
  };

  const openActions = (deal) => {
    setSelectedDeal(deal);
    setOpenActionMenu(deal.dealUID);
  };

  const handleCopyPhone = async (leadId, phoneNo) => {
    if (!phoneNo) return;

    try {
      await navigator.clipboard.writeText(phoneNo);
      setCopiedLeadId(leadId);

      window.setTimeout(() => {
        setCopiedLeadId((current) => (current === leadId ? null : current));
      }, 1800);
    } catch (err) {
      console.error("Failed to copy phone number:", err);
    }
  };

  const displayedTotalRecords = searchPerformed
    ? localResults.length
    : totalRecords;

  const totalPages = Math.ceil(displayedTotalRecords / pageSize);

  const startIndex = (page - 1) * pageSize;

  const endIndex = startIndex + pageSize;

  const chunkOffset = ((page - 1) * pageSize) % 500;

  const displayedDeals = searchPerformed
    ? localResults.slice(startIndex, endIndex)
    : deals.slice(chunkOffset, chunkOffset + pageSize);

  const handlePageChange = async (newPage) => {
    if (chunkLoading) return;

    setPage(newPage);

    const recordPosition = newPage * pageSize;

    const requiredChunk = Math.ceil(recordPosition / 500);

    if (requiredChunk !== chunkNumber) {
      await fetchDeals(requiredChunk, false, activeSearch);
    }
  };

  const DealsSkeleton = ({ rows = 8 }) => {
    return (
      <SkeletonTheme baseColor="#f1f5f9" highlightColor="#ffffff">
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="
            grid grid-cols-12
            gap-4
            px-6 py-4
            border-b border-slate-100
          "
          >
            {/* Candidate */}
            <div className="col-span-3 flex gap-3">
              <Skeleton circle width={40} height={40} />

              <div className="flex-1">
                <Skeleton height={16} width="70%" />
                <Skeleton height={12} width="90%" className="mt-2" />
              </div>
            </div>

            {/* Company */}
            <div className="col-span-2">
              <Skeleton height={16} />
            </div>

            {/* Role */}
            <div className="col-span-1">
              <Skeleton height={16} />
            </div>

            {/* Status */}
            <div className="col-span-1">
              <Skeleton height={24} />
            </div>

            {/* Owner */}
            <div className="col-span-1">
              <Skeleton height={16} />
            </div>

            {/* CTC */}
            <div className="col-span-1">
              <Skeleton height={16} />
            </div>

            {/* Updated */}
            <div className="col-span-2">
              <Skeleton height={16} />
              <Skeleton height={12} width="70%" className="mt-2" />
            </div>

            {/* Actions */}
            <div className="col-span-1 flex justify-center">
              <Skeleton width={32} height={32} />
            </div>
          </div>
        ))}
      </SkeletonTheme>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return { date: "-", time: "-" };
    }

    const dateObj = new Date(dateString);

    if (isNaN(dateObj.getTime())) {
      return { date: "-", time: "-" };
    }

    return {
      date: dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: dateObj.toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const handleActionClick = (deal, action) => {
    setSelectedDeal(deal);
    setSelectedAction(action);

    console.log("Deal:", deal.dealUID);
    console.log("Action:", action);
  };

  const getAvailableActions = (deal) => {
    let actions = [...(DEAL_ACTIONS[deal.dealStatus] || [])];

    if (!deal.canDoBGV) {
      actions = actions.filter((x) => x !== "BGV");
    }

    if (!deal.canReleaseOffer) {
      actions = actions.filter((x) => x !== "Release Offer");
    }

    if (!deal.canHire) {
      actions = actions.filter((x) => x !== "Hire");
    }

    return actions;
  };

  return (
    <>
      <div className="p-2 space-y-5">
        {/* ================= TABLE TOOLBAR ================= */}
        <div
          className="max-w-8xl mx-auto bg-gradient-to-b from-white to-slate-50
              border border-slate-200/70
              shadow-sm
              hover:shadow-md
              hover:border-blue-200 rounded-xl px-5 py-3"
        >
          <div className="flex items-center justify-between gap-4">
            {/* LEFT */}
            <div className="flex items-center gap-4 flex-1">
              {/* SEARCH */}
              <div className="flex items-center w-full max-w-md bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                <input
                  value={searchF}
                  onChange={(e) => setSearchF(e.target.value)}
                  placeholder="Search deals by leaad name, phone, company and role..."
                  className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleLocalSearch();
                    }
                  }}
                />

                <button
                  title="Search"
                  onClick={handleLocalSearch}
                  className="h-10 w-10 border-l border-slate-200 flex items-center justify-center hover:bg-slate-100 transition"
                >
                  <Search size={17} className="text-slate-500" />
                </button>

                {(searchF || activeSearch || searchPerformed) && (
                  <button
                    title="Reset"
                    onClick={handleReset}
                    className="h-10 w-10 border-l border-slate-200 flex items-center justify-center hover:bg-slate-100 transition"
                  >
                    <X size={16} className="text-red-500" />
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-5">
              {/* SHOWING */}
              <div className="text-sm text-slate-600 whitespace-nowrap">
                Showing{" "}
                <span className="font-semibold text-slate-800">
                  {displayedTotalRecords === 0
                    ? "0"
                    : `${startIndex + 1}-${Math.min(
                        endIndex,
                        displayedTotalRecords,
                      )}`}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-800">
                  {displayedTotalRecords}
                </span>
              </div>

              {/* ROWS */}
              <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
                <span className="text-sm text-slate-500 whitespace-nowrap">
                  Rows
                </span>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    const rows = Number(e.target.value);

                    setPageSize(rows);

                    setPage(1);

                    if (rows > deals.length && chunkNumber < totalChunks) {
                      setChunkNumber(chunkNumber + 1);
                    }
                  }}
                  className="h-8 px-2 pr-7 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 focus:outline-none"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={125}>125</option>
                  <option value={250}>250</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* DEALS TABLE */}
        <div className="max-w-8xl mx-auto bg-white border border-border rounded-xl shadow-sm flex flex-col h-[calc(100vh-220px)] overflow-visible">
          {/* HEADER */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sticky top-0 z-20 flex-shrink-0">
            <div className="col-span-2 px-[48px]">Candidate</div>
            <div className="col-span-1">Phone</div>
            <div className="col-span-2">Target Company</div>
            <div className="col-span-2">Target Role</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Deal Owner</div>
            <div className="col-span-1">Offered CTC</div>
            <div className="col-span-1 text-right">Created Date</div>
            <div className="col-span-1 text-center">Actions</div>
          </div>

          {/* BODY */}
          <div className="relative flex-1 overflow-y-auto divide-y-2 divide-slate-100">
            {chunkLoading && (
              <div className="absolute inset-0 z-40 bg-white/80 backdrop-blur-sm">
                <DealsSkeleton rows={pageSize} />
              </div>
            )}

            {initialLoading ? (
              <DealsSkeleton rows={pageSize} />
            ) : deals.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                    <User size={24} className="text-slate-400" />
                  </div>

                  <h3 className="text-sm font-semibold text-slate-700">
                    No Deals Found
                  </h3>
                </div>
              </div>
            ) : (
              displayedDeals.map((deal) => {
                const createdFormatted = formatDate(deal.createdAt);
                const updatedFormatted = formatDate(deal.lastUpdatedAt);

                return (
                  <div key={deal.dealUID}>
                    {/* MAIN ROW */}
                    <div className="grid grid-cols-12 gap-4 px-6 py-4 items-start hover:bg-blue-50/70 transition-all duration-200 group">
                      {/* CANDIDATE */}
                      <div className="col-span-2 flex items-start gap-3 min-w-0">
                        <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center font-semibold text-sm text-blue-700">
                          {deal.name?.charAt(0)}
                        </div>

                        <div
                          onClick={() =>
                            setExpandedDeal(
                              expandedDeal === deal.dealUID
                                ? null
                                : deal.dealUID,
                            )
                          }
                          className="min-w-0 pt-0.5 cursor-pointer"
                        >
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-[15px] truncate text-blue-600">
                              {deal.name}
                            </p>

                            <ChevronDown
                              size={14}
                              className={`text-slate-400 transition-transform duration-300 ${
                                expandedDeal === deal.dealUID
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          </div>

                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {deal.email}
                          </p>
                        </div>
                      </div>

                      {/* PHONE */}
                          <div className="col-span-1 pt-1">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                              <Phone size={13} className="text-slate-400" />

                              <span
                                className="transition-colors font-medium tracking-wide"
                                title={deal.phoneNo}
                              >
                                {deal.phoneNo}
                              </span>

                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyPhone(
                                    deal.leadUID , deal.phoneNo,
                                  )
                                }
                                className={`inline-flex h-6 w-6 items-center justify-center rounded-lg border transition ${
                                  copiedLeadId === (deal.leadUID)
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                                    : "border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                }`}
                                title={
                                  copiedLeadId === (deal.leadUID)
                                    ? "Copied"
                                    : "Copy phone number"
                                }
                                aria-label={`Copy phone number for ${deal.name}`}
                              >
                                {copiedLeadId === (deal.leadUID) ? (
                                  <Check size={12} />
                                ) : (
                                  <Copy size={12} />
                                )}
                              </button>
                            </div>
                          </div>

                      {/* COMPANY */}
                      <div className="col-span-2 pt-0.5 min-w-0">
                        <p
                          className="text-sm text-slate-700 truncate"
                          title={deal.targetOrganization}
                        >
                          {deal.targetOrganization || "-"}
                        </p>
                      </div>

                      {/* ROLE */}
                      <div className="col-span-2 pt-0.5 min-w-0">
                        <p
                          className="text-sm text-slate-700 truncate"
                          title={deal.targetRole}
                        >
                          {deal.targetRole || "-"}
                        </p>
                      </div>

                      {/* STATUS */}
                      <div className="col-span-1 pt-0.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border ${getStatusBadgeClass(deal.dealStatus)}`}
                        >
                          {deal.dealStatus}
                        </span>
                      </div>

                      {/* OWNER */}
                      <div className="col-span-1 pt-1">
                        <span className="text-sm text-slate-600">
                          {deal.ownerName || "-"}
                        </span>
                      </div>

                      {/* CTC */}
                      <div className="col-span-1 pt-1">
                        <span className="text-sm font-medium text-slate-700">
                          {deal.offeredCTC
                            ? `₹ ${formatCTC(deal.offeredCTC)}`
                            : "-"}
                        </span>
                      </div>

                      {/* Created AT */}
                      <div className="col-span-1 flex items-start justify-end">
                        <div className="text-right pt-0.5">
                          <p className="text-sm text-slate-700 leading-none whitespace-nowrap">
                            {createdFormatted.date}
                          </p>

                          <p className="text-xs text-slate-400 mt-1 whitespace-nowrap">
                            {createdFormatted.time}
                          </p>
                        </div>
                      </div>

                      {/* ACTION */}
                      <div className="col-span-1 flex justify-center relative">
                        {deal.dealStatus === "Rejected" ||
                        deal.dealStatus === "Backout" ? (
                          <div
                            title="No further actions available"
                            className="h-7 w-7 rounded-lg border border-slate-300 bg-slate-100 flex items-center justify-center"
                          >
                            <Lock size={14} className="text-slate-600" />
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                setOpenActionMenu(
                                  openActionMenu === deal.dealUID
                                    ? null
                                    : deal.dealUID,
                                )
                              }
                              className="h-8 w-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center"
                            >
                              <MoreVertical size={15} />
                            </button>

                            {openActionMenu === deal.dealUID && (
                              <div
                                ref={actionMenuRef}
                                className="absolute top-10 right-0 bg-white border rounded-lg shadow-lg z-50 min-w-[180px]"
                              >
                                {getAvailableActions(deal).map((action) => (
                                  <button
                                    key={action}
                                    className="block w-full text-left px-4 py-2 hover:bg-slate-100"
                                    onClick={() => {
                                      setSelectedDeal(deal);
                                      setSelectedAction(action);

                                      setOpenDrawer(true);

                                      setOpenActionMenu(null);
                                    }}
                                  >
                                    {action}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* EXPANDED SECTION */}
                    <div
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                        expandedDeal === deal.dealUID
                          ? "max-h-[1200px] opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="px-6 pb-3">
                        <div className="ml-13 rounded-xl p-[1px]">
                          <div
                            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#edf4fb] via-[#f8fbff] to-white
                                       border border-slate-200/70 shadow-[0_6px_24px_rgba(148,163,184,0.08)]"
                          >
                            <div className="px-5 py-4">
                              {/* QUICK STATS (KEEP YOUR ORIGINAL STYLE, JUST ENRICHED) */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                                <div className="rounded-xl bg-white/80 border px-4 py-2.5">
                                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                    Status
                                  </p>
                                  <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                    {deal.dealStatus}
                                  </p>
                                </div>

                                <div className="rounded-xl bg-white/80 border px-4 py-2.5">
                                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                    Offered CTC
                                  </p>
                                  <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                    {deal.offeredCTC ? `₹ ${formatCTC(deal.offeredCTC)}` : "-"}
                                  </p>
                                </div>

                                <div className="rounded-xl bg-white/80 border px-4 py-2.5">
                                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                    Company
                                  </p>
                                  <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                    {deal.targetOrganization || "-"}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {deal.targetRole || "-"}
                                  </p>
                                </div>

                                <div className="rounded-xl bg-white/80 border px-4 py-2.5">
                                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                                    Owner
                                  </p>
                                  <p className="text-sm font-semibold text-slate-800 mt-1.5">
                                    {deal.ownerName || "-"}
                                  </p>
                                </div>
                              </div>

                              {/* DETAILS (ENRICHED, SAME STRUCTURE YOU HAD) */}
                              <div className="rounded-3xl bg-white/70 backdrop-blur-md border overflow-hidden">
                                <div className="divide-y divide-slate-200/50">
                                  {/* ROW 1 */}
                                  <div className="grid md:grid-cols-2">
                                    <div className="px-4 py-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Candidate
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.name}
                                      </p>
                                    </div>

                                    <div className="px-4 py-3 border-l border-slate-200/50">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Contact
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.email}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {deal.phoneNo || "-"}
                                      </p>
                                    </div>
                                  </div>

                                  {/* ROW 2 */}
                                  <div className="grid md:grid-cols-3">
                                    <div className="px-4 py-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Target Company
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.targetOrganization || "-"}
                                      </p>
                                    </div>

                                    <div className="px-4 py-3 border-l border-slate-200/50">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Target Role
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.targetRole || "-"}
                                      </p>
                                    </div>

                                    <div className="px-4 py-3 border-l border-slate-200/50">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Source
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.source || "-"}
                                      </p>
                                    </div>
                                  </div>

                                  {/* ROW 3 (LIFECYCLE - THIS WAS MISSING BEFORE) */}
                                  <div className="grid md:grid-cols-3">
                                    <div className="px-4 py-3">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        CV Shared On
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.cvSentDate
                                          ? new Date(
                                              deal.cvSentDate,
                                            ).toLocaleString("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              hour12: true,
                                            })
                                          : "-"}
                                      </p>
                                    </div>

                                    <div className="px-4 py-3 border-l border-slate-200/50">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Interview Scheduled On
                                      </p>
                                      <p className="text-sm font-medium text-slate-700 mt-1">
                                        {deal.interviewDate
                                          ? new Date(
                                              deal.interviewDate,
                                            ).toLocaleString("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              hour12: true,
                                            })
                                          : "-"}
                                      </p>
                                    </div>

                                    <div className="px-4 py-3 border-l border-slate-200/50">
                                      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                        Joining Timeline
                                      </p>

                                      <div className="mt-2 space-y-2">
                                        {/* Expected */}
                                        <div className="grid grid-cols-2">
                                          <span className="text-[12px] text-slate-500">
                                            Expected
                                          </span>
                                          <span className="text-[12px] font-medium text-slate-700 ">
                                            {deal.expectedJoiningDate
                                              ? new Date(
                                                  deal.expectedJoiningDate,
                                                ).toLocaleDateString("en-IN", {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                                })
                                              : "-"}
                                          </span>
                                        </div>

                                        {/* Actual */}
                                        <div className="grid grid-cols-2">
                                          <span className="text-[12px] text-slate-500">
                                            Actual
                                          </span>
                                          <span className="text-[12px] font-medium text-slate-700">
                                            {deal.joiningDate
                                              ? new Date(
                                                  deal.joiningDate,
                                                ).toLocaleDateString("en-IN", {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                                })
                                              : "-"}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* FOOTER */}
                                  <div className="px-4 py-3 flex items-center justify-between border-t border-slate-100 bg-slate-50/60">
                                    <div className="text-xs text-slate-500">
                                      <span className="font-medium text-slate-600">
                                        Last Updated:
                                      </span>{" "}
                                      {deal.lastUpdatedAt
                                        ? new Date(
                                            deal.lastUpdatedAt,
                                          ).toLocaleString("en-IN")
                                        : "-"}
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => {
                                          setSelectedResume(deal);
                                          setOpenResumeDrawer(true);
                                        }}
                                        className="
        h-9 px-4
        rounded-lg
        border border-slate-200
        bg-white
        text-sm font-medium text-slate-700
        hover:bg-slate-50
        hover:border-slate-300
        transition-all
      "
                                      >
                                        View Resume
                                      </button>

                                      <button
                                        onClick={() => {
                                          onViewHistory(deal);
                                        }}
                                        className="
        h-9 px-4
        rounded-lg
        bg-blue-600
        text-sm font-medium text-white
        hover:bg-blue-700
        transition-all
      "
                                      >
                                        View History
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {searchPerformed && !activeSearch && displayedDeals.length > 0 && (
            <div className="border-t border-slate-200 bg-slate-50 px-6 py-5">
              <div className="flex justify-center">
                <button
                  onClick={searchDatabase}
                  disabled={dbSearching}
                  className="h-9 px-4 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
                >
                  {dbSearching ? "Loading..." : "Load More"}
                </button>
              </div>
            </div>
          )}

          {/* PAGINATION */}
          {displayedTotalRecords > 0 && (
            <div className="flex items-center justify-end px-6 py-3 border-t border-slate-200 bg-white sticky bottom-0 flex-shrink-0">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                {/* PREV */}
                <button
                  disabled={page === 1}
                  onClick={() => handlePageChange(page - 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
        ${
          page === 1
            ? "text-slate-300 cursor-not-allowed"
            : "text-slate-500 hover:bg-slate-100"
        }`}
                >
                  Prev
                </button>

                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm font-medium text-slate-600">
                            ...
                          </span>
                        )}

                        <button
                          onClick={() => handlePageChange(pageNumber)}
                          className={`h-8 min-w-[34px] px-2.5 rounded-lg text-sm font-semibold transition-all duration-200
                ${
                  page === pageNumber
                    ? "bg-primary text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                {/* NEXT */}
                <button
                  disabled={page === totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
        ${
          page === totalPages
            ? "text-slate-300 cursor-not-allowed"
            : "text-slate-500 hover:bg-slate-100"
        }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DealActionDrawer
        open={openDrawer}
        deal={selectedDeal}
        action={selectedAction}
        onClose={() => setOpenDrawer(false)}
        onSubmit={async (payload) => {
          try {
            let response = null;

            switch (selectedAction) {
              case "Shortlist":
                response = await ShortlistCandidate(payload);
                break;

              case "Reject":
                response = await RejectCandidate(payload);
                break;

              case "Backout":
                response = await BackoutCandidate(payload);
                break;

              case "Schedule Interview":
                response = await SheduleInterview(payload);
                break;

              case "Select":
                response = await SelectCandidate(payload);
                break;

              case "BGV":
                response = await MoveCandidateToBGV(payload);
                break;

              case "Release Offer":
                response = await ReleaseCandidateOffer(payload);
                break;

              case "Hire":
                response = await HireCandidate(payload);
                break;

              default:
                break;
            }

            setAlert({
              type: "success",
              message:
                response?.message ||
                `${selectedAction} completed successfully.`,
              duration: 1500,
            });

            setOpenDrawer(false);

            fetchDeals(chunkNumber, true);
          } catch (error) {
            setAlert({
              type: "error",
              message:
                error?.response?.data?.message ||
                error?.message ||
                `Failed to ${selectedAction}`,
              duration: 2000,
            });
          }
        }}
      />

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert.type}
          message={alert.message}
          duration={alert.duration || 1500}
          onClose={() => setAlert(null)}
        />
      )}

      <DealHistoryDrawer
        open={!!historyDeal}
        deal={historyDeal}
        onClose={() => setHistoryDeal(null)}
      />

      <ViewResumeDrawer
        open={openResumeDrawer}
        onClose={() => {
          setOpenResumeDrawer(false);
          setSelectedResume(null);
        }}
        resumeUrl={selectedResume?.resumePath}
        candidateName={selectedResume?.name}
      />
    </>
  );
};

export default MyDeals;
